import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { fieldSaga } from './saga';
import {
  FieldState,
  IDeleteFieldPayload,
  IDrawnPolygon,
  ISetEditFieldPayload,
  ISubmitNewFieldsPayload,
  ITempFieldListItem,
  ISubmitEditFieldDataPayload,
  IGetFieldNotesPayload,
  IGetLayerByShapeIdPayload,
  ISubmitImportFieldsFromSeasonPayload,
  IGetFieldPropertiesPayload,
} from './types';

export const initialState: FieldState = {
  allRoutesFilters: {
    startDate: undefined,
    endDate: undefined,
  },

  tempFieldsList: [],
  shapeFileId: null,
  importFieldsFromSeason: {
    loading: false,
    isOpen: false,
  },
  editForm: { isOpen: false, loading: false },
  deleteFieldLoading: false,
  drawnFields: {
    polygonsArr: [],
  },
  editField: {
    latlngs: [],
    fieldId: null,
    name: null,
    companyId: null,
    seasonId: null,
  },
  fieldNotes: '',
  fieldProperties: {},
};

const slice = createSlice({
  name: 'field',
  initialState,
  reducers: {
    setAllRoutesFilters(
      state,
      action: PayloadAction<{ startDate?: string; endDate?: string }>,
    ) {
      state.allRoutesFilters = action.payload;
    },

    toggleImportFieldsFromSeasonModal(state, action: PayloadAction<boolean>) {
      state.importFieldsFromSeason.isOpen = action.payload;
    },
    submitImportFieldsFromSeason(
      state,
      action: PayloadAction<ISubmitImportFieldsFromSeasonPayload>,
    ) {},
    setImportFieldsFromSeasonLoading(state, action: PayloadAction<boolean>) {
      state.importFieldsFromSeason.loading = action.payload;
    },

    setEditFieldData(state, action: PayloadAction<ISetEditFieldPayload>) {
      state.editField = { ...state.editField, ...action.payload };
    },

    setEditFieldLatLngs(state, action: PayloadAction<L.LatLng[]>) {
      state.editField.latlngs = action.payload;
    },

    setDrawnFieldsArr(state, action: PayloadAction<IDrawnPolygon[]>) {
      state.drawnFields.polygonsArr = action.payload;
    },

    deleteField(state, action: PayloadAction<IDeleteFieldPayload>) {},
    setDeleteFieldLoading(state, action: PayloadAction<boolean>) {
      state.deleteFieldLoading = action.payload;
    },

    toggleEditFieldDataForm(state, action: PayloadAction<boolean>) {
      state.editForm.isOpen = action.payload;
    },

    getFieldNotes(state, action: PayloadAction<IGetFieldNotesPayload>) {},
    setFieldNotes(state, action: PayloadAction<string>) {
      state.fieldNotes = action.payload;
    },

    getFieldProperties(
      state,
      action: PayloadAction<IGetFieldPropertiesPayload>,
    ) {},
    setFieldProperties(
      state,
      action: PayloadAction<{ [key: string]: string | number }>,
    ) {
      state.fieldProperties = action.payload;
    },

    submitEditFieldData(
      state,
      action: PayloadAction<ISubmitEditFieldDataPayload>,
    ) {},

    setEditFieldLoading(state, action: PayloadAction<boolean>) {
      state.editForm.loading = action.payload;
    },

    getLayerByShapeId(
      state,
      action: PayloadAction<IGetLayerByShapeIdPayload>,
    ) {},

    setTempFieldsList(state, action: PayloadAction<ITempFieldListItem[]>) {
      state.tempFieldsList = action.payload;
    },
    setShapeFileId(state, action: PayloadAction<string>) {
      state.shapeFileId = action.payload;
    },

    submitNewFields(state, action: PayloadAction<ISubmitNewFieldsPayload>) {},

    clearState(state) {
      return initialState;
    },
  },
});

export const { actions: fieldActions } = slice;

export const useFieldSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: fieldSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useFieldSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
